import type { Brand, FormFieldConfig } from "./brand.types.js";
import type { EspaceProfile } from "./espace.types.js";

/**
 * L'espace école : ce qu'un référent voit et ce qu'il peut changer.
 *
 * Le périmètre est celui déjà écrit dans le CLAUDE.md, et il ne s'élargit pas ici :
 * une école change son ILLUSTRATION, son LOGO, sa COULEUR, ses TEXTES et ses
 * CHAMPS. La police, la géométrie et la palette complète ont été retirées une fois
 * chacune, parce qu'elles permettaient d'abîmer une page sans en améliorer aucune.
 *
 * ⚠️ Le logo et l'illustration ne sont PAS éditables ici, et ce n'est pas un
 * oubli : ils viennent de main-app, où l'école les gère déjà. Deux endroits pour
 * téléverser la même image donneraient deux images qui divergent — et il n'y a de
 * toute façon aucun stockage de fichier dans ce projet.
 */

/** Une école, avec l'identifiant dont l'écran a besoin pour écrire. */
export type EcoleSchool = Brand & { brandId: string };

export type EcoleOverview = {
	admin: { email: string; fullName: string | null };
	/** Une entrée par accès. 11 référents en ont plusieurs : les groupes. */
	schools: EcoleSchool[];
};

/** Ce qu'une école a le droit d'écrire. Tout le reste est absent, pas ignoré. */
export type EcoleConfig = Partial<{
	navBase: string | null;
	baseline: string | null;
	pageTitle: string | null;
	pageIntro: string | null;
	confirmationMessage: string | null;
	contactName: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	formFields: FormFieldConfig[];
}>;

/** Les plafonds, en un seul endroit : le serveur les impose, l'écran les affiche. */
export const ECOLE_LIMITES = {
	baseline: 200,
	pageTitle: 80,
	pageIntro: 600,
	confirmationMessage: 300,
	contactName: 120,
	contactPhone: 40,
} as const;

// ── Le versant école : les offres reçues et les profils envoyés ──────────────────
//
// Ce que l'école tranche et envoie, vu depuis SON écran. Les deux sens du tuyau
// (CLAUDE.md) se lisent ici à l'envers de l'espace entreprise : l'offre arrive, la
// sélection part.

/** Une offre déposée chez l'école, avec qui l'a déposée. */
export type EcoleOffer = {
	id: string;
	job: string;
	description: string | null;
	location: string | null;
	contract: string | null;
	telework: string | null;
	qualification: string | null;
	startsOn: string | null;
	headcount: number | null;
	/** Le statut EFFECTIF (`statutEffectif`) : une offre publiée et expirée vaut `archivee`. */
	status: string;
	schoolNote: string | null;
	createdAt: string;
	expiresOn: string | null;
	decidedAt: string | null;
	publishedAt: string | null;
	companyName: string | null;
	contact: { fullName: string | null; email: string; phone: string | null };
};

/**
 * Un profil envoyé, et la réponse de l'entreprise : la MÊME forme que celle que
 * l'entreprise lit. Deux types pour une ligne de `shared_profile` divergeraient.
 */
export type EcoleSharedProfile = EspaceProfile;

export type EcoleSelection = {
	id: string;
	title: string;
	message: string | null;
	offerRequestId: string | null;
	offerJob: string | null;
	createdAt: string;
	closedAt: string | null;
	contact: { fullName: string | null; email: string; companyName: string | null };
	profiles: EcoleSharedProfile[];
};

/** Une lecture pour les deux écrans, comme l'espace entreprise. */
export type EcoleActivite = { offers: EcoleOffer[]; selections: EcoleSelection[] };

/** Un profil tel que l'école le recopie : ce qu'elle accepte de montrer, rien d'autre. */
export type NouveauProfil = {
	fullName: string;
	headline?: string | null;
	program?: string | null;
	location?: string | null;
	availability?: string | null;
	cvUrl?: string | null;
};

/**
 * Une sélection part vers UNE entreprise : celle d'une offre (`offerId`), ou une
 * adresse (`email`). La seconde forme est l'invitation d'une entreprise dans
 * l'espace : elle y entre ensuite par son email, sans compte.
 */
export type NouvelleSelection = {
	offerId?: string | null;
	email?: string | null;
	contactName?: string | null;
	title: string;
	message?: string | null;
	profiles: NouveauProfil[];
};

/** Les plafonds du versant école, imposés par le serveur et affichés par l'écran. */
export const VERSANT_LIMITES = {
	motif: 600,
	selectionTitle: 120,
	selectionMessage: 1000,
	profils: 20,
	profilNom: 120,
	profilChamp: 160,
	contactName: 120,
	// Un lien vers le CV : https seulement, le serveur refuse le reste.
	cvUrl: 500,
} as const;
